/**
 * Field-level validation enforcement.
 *
 * The form builder lets users configure pattern, minLength/maxLength, and
 * numeric min/max on fields. Historically these were stored in the schema but
 * never enforced at runtime — the Validation panel was decorative. This module
 * provides a single `validateFieldValue` helper used by DynamicField (inline
 * errors) and the submit gates (block submission) in both the operational form
 * modal and the public form renderer.
 */

export interface FieldValidationRule {
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  message?: string;
}

export interface FieldValidationResult {
  valid: boolean;
  message?: string;
}

/** True when the value should be considered empty for required-style checks. */
export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Validate a single field value against its validation rules.
 * Empty values are treated as valid here — required-ness is enforced
 * separately by the section-completion logic.
 */
export function validateFieldValue(
  value: unknown,
  rules?: FieldValidationRule,
): FieldValidationResult {
  if (!rules) return { valid: true };
  if (isEmptyValue(value)) return { valid: true };

  // Numeric min/max (number, measurement)
  const numericValue = typeof value === 'object' && value !== null && 'value' in value && !Array.isArray(value)
    ? Number((value as Record<string, unknown>).value)
    : Number(value);

  if (rules.min !== undefined && !isNaN(numericValue) && numericValue < rules.min) {
    return { valid: false, message: rules.message || `Value must be at least ${rules.min}.` };
  }
  if (rules.max !== undefined && !isNaN(numericValue) && numericValue > rules.max) {
    return { valid: false, message: rules.message || `Value must be at most ${rules.max}.` };
  }

  const strValue = Array.isArray(value) ? value.join(', ') : String(value);

  // String length constraints
  if (rules.minLength !== undefined && strValue.length < rules.minLength) {
    return { valid: false, message: rules.message || `Must be at least ${rules.minLength} characters.` };
  }
  if (rules.maxLength !== undefined && strValue.length > rules.maxLength) {
    return { valid: false, message: rules.message || `Must be at most ${rules.maxLength} characters.` };
  }

  // Regex pattern
  if (rules.pattern) {
    try {
      const re = new RegExp(rules.pattern);
      if (!re.test(strValue)) {
        return { valid: false, message: rules.message || 'Value does not match the required pattern.' };
      }
    } catch {
      // Invalid pattern in schema — ignore rather than block submission
    }
  }

  return { valid: true };
}

/**
 * Validate all fields in a set of sections against current form data.
 * Returns a map of fieldKey -> error message for invalid fields.
 * Skips fields that are hidden (rules-engine or conditional logic) since
 * the caller is expected to pass only visible fields.
 */
export function collectFieldValidationErrors(
  sections: Array<{ fields?: Array<{ key: string; validation?: FieldValidationRule; type?: string }> }>,
  formData: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const section of sections) {
    for (const field of section.fields || []) {
      const result = validateFieldValue(formData[field.key], field.validation);
      if (!result.valid && result.message) {
        errors[field.key] = result.message;
      }
    }
  }
  return errors;
}

/**
 * Collect required fields that are empty/missing in the form data.
 *
 * This is the "section-completion logic" referenced by validateFieldValue's
 * doc comment — required-ness is enforced here rather than in the per-field
 * validator. Used by the public form submit gate (both client and server)
 * and any other path that needs a simple list of missing required fields.
 *
 * Only fields present in the provided sections are checked, so callers should
 * pass only *visible* sections (after rules-engine / branching filtering) to
 * avoid blocking submission on fields the respondent never sees.
 *
 * Returns an array of { key, label } for each missing required field.
 */
export function collectMissingRequiredFields(
  sections: Array<{
    fields?: Array<{
      key: string;
      label?: string;
      is_required?: boolean;
      required?: boolean;
    }>;
  }>,
  formData: Record<string, unknown>,
): Array<{ key: string; label: string }> {
  const missing: Array<{ key: string; label: string }> = [];
  for (const section of sections) {
    for (const field of section.fields || []) {
      const isRequired = !!(field.is_required || field.required);
      if (!isRequired) continue;
      if (isEmptyValue(formData[field.key])) {
        missing.push({ key: field.key, label: field.label || field.key });
      }
    }
  }
  return missing;
}
